import { MdPhoneCallback, MdEmail } from "react-icons/md";
import { FaWhatsapp, FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaYoutube } from "react-icons/fa";
import Container from "../../ui/container/Container";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ABOUT_ROUTE,
  BUYING_FAQ_ROUTE,
  CONTACT_ROUTE,
  PRIVACY_POLICY_ROUTE,
  REFUND_POLICY_ROUTE,
  SHIPPING_POLICY_ROUTE,
  SHOP_ROUTE,
  TERMS_CONDITION_ROUTE,
  WARRANTY_POLICY_ROUTE,
} from "../../../routes/web/rootRoutes";
import ShareButton from "./ShareButton";

const Footer = () => {
  const setting = useSelector((state) => state.globalState.siteSetting);

  const socialLinks = [
    { icon: FaFacebook, url: setting?.social_media?.facebook, color: "hover:text-blue-500" },
    { icon: FaInstagram, url: setting?.social_media?.instagram, color: "hover:text-pink-500" },
    { icon: FaTwitter, url: setting?.social_media?.twitter, color: "hover:text-sky-400" },
    { icon: FaLinkedin, url: setting?.social_media?.linkedin, color: "hover:text-blue-600" },
    { icon: FaYoutube, url: setting?.social_media?.youtube, color: "hover:text-red-500" },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-300 relative">
      <ShareButton />
      
      <Container>
        <div className="py-12">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
            
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600 mb-2">
                  {setting?.site_settings?.site_name || "Your Brand"}
                </h2>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Delivering quality and excellence right to your doorstep. Your trusted partner for all your needs.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-gradient-to-b from-orange-500 to-orange-600 rounded"></span>
                  Follow Us
                </h3>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return social.url ? (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-gray-600 ${social.color}`}
                      >
                        <Icon className="text-lg" />
                      </a>
                    ) : null;
                  })}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-gradient-to-b from-orange-500 to-orange-600 rounded"></span>
                Contact Us
              </h3>
              <div className="space-y-3">
                <a 
                  href={`tel:${setting?.support_info?.support_mobile}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all duration-300 group"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MdPhoneCallback className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Call Us</p>
                    <p className="text-white font-medium">{setting?.support_info?.support_mobile}</p>
                  </div>
                </a>

                <a 
                  href={`https://wa.me/${setting?.support_info?.whatsapp_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all duration-300 group"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FaWhatsapp className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">WhatsApp</p>
                    <p className="text-white font-medium">{setting?.support_info?.whatsapp_number}</p>
                  </div>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-gradient-to-b from-orange-500 to-orange-600 rounded"></span>
                Quick Links
              </h3>
              <ul className="space-y-2">
                {[
                  { to: ABOUT_ROUTE, label: "About Us" },
                  { to: CONTACT_ROUTE, label: "Contact Us" },
                  { to: BUYING_FAQ_ROUTE, label: "FAQs" },
                  { to: SHOP_ROUTE, label: "Shop" },
                ].map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="flex items-center gap-2 text-gray-400 hover:text-orange-400 transition-colors duration-200 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-gradient-to-b from-orange-500 to-orange-600 rounded"></span>
                Policies
              </h3>
              <ul className="space-y-2">
                {[
                  { to: PRIVACY_POLICY_ROUTE, label: "Privacy Policy" },
                  { to: TERMS_CONDITION_ROUTE, label: "Terms & Conditions" },
                  { to: REFUND_POLICY_ROUTE, label: "Refund Policy" },
                  { to: SHIPPING_POLICY_ROUTE, label: "Shipping Policy" },
                ].map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="flex items-center gap-2 text-gray-400 hover:text-orange-400 transition-colors duration-200 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700"></div>

        <div className="py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <div className="text-center md:text-left">
              <p className="text-gray-400">
                © 2026 All rights reserved.{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600 font-semibold">
                  {setting?.site_settings?.site_name}
                </span>
              </p>
            </div>
            
            <div className="text-center md:text-right text-xs text-gray-500 space-y-1">
              <p>{setting?.support_info?.company}</p>
              {/* <p>CIN: {setting?.support_info?.cin}</p> */}
            </div>
          </div>
        </div>
      </Container>

      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500"></div>
    </footer>
  );
};

export default Footer;